import React, { memo, useCallback, useState } from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import Button from '@atlaskit/button/standard-button';
import Heading from '@atlaskit/heading';
import DocumentIcon from '@atlaskit/icon/glyph/document';
import { Text } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import Tooltip from '@atlaskit/tooltip';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { CopyButton } from '@atlassian/bitkit-copy';

import {
  FileTree,
  FileTreeProps,
  FileClickProps,
  TreeEntry,
} from 'src/components/file-tree';
import GenericMessage from 'src/components/generic-message';
import { ExpanderOnChangeEvent } from 'src/components/sidebar';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';
import commonMessages from 'src/i18n/common';
import {
  FileViewMode,
  getGlobalFileViewMode,
  getGlobalIsColorBlindModeEnabled,
} from 'src/redux/diff-settings';
import {
  getPullRequestSourceHash,
  getPullRequestDestinationHash,
} from 'src/redux/pull-request/selectors';
import { usePrFileViewFileAnalytics } from 'src/sections/repository/sections/pull-request/hooks/analytics';
import { scmCommandCode } from 'src/sections/repository/utils/formatted-scm';
import { shortHash } from 'src/utils/short-hash';

import { FileList } from '../file-list/src/file-list';
import { isFile } from '../file-tree/src/flatten-directories';

import messages from './file-tree-card.i18n';
import * as styles from './file-tree-card.style';
import { FilterControl } from './filter-control';

export const FILE_TREE_CARD_COLLAPSED_LOCALSTORAGE_KEY =
  'file.tree.card.collapsed';
export type FileTreeCardStateProps = {
  hasConflicts?: boolean;
  hasError?: boolean;
  isTruncated?: boolean;
  untruncatedFileCount: number;
  fileTree: TreeEntry[] | null;
};

export type FileTreeCardExternalProps = Omit<
  FileTreeProps,
  'filteredDiffStat'
> & {
  isCollapsed?: boolean;
  onFileClick?: (event: React.MouseEvent, props: FileClickProps) => void;
  onChange: (event: ExpanderOnChangeEvent) => boolean | undefined;
  onErrorClick?: () => void;
};

export type FileTreeCardProps = FileTreeCardStateProps &
  FileTreeCardExternalProps;

export const FileTreeCard = memo((props: FileTreeCardProps) => {
  const {
    isCollapsed,
    hasConflicts,
    fileTree,
    onFileClick,
    hasError,
    isTruncated,
    onErrorClick,
    untruncatedFileCount,
  } = props;
  const intl = useIntl();

  const isChangeFileTreeFilteringEnabled = useStatsigGate(
    StatsigFeatureKeys.isChangeFileTreeFilteringEnabled
  );

  const icon = <DocumentIcon label={intl.formatMessage(messages.label)} />;
  const shouldShowFileList =
    useSelector(getGlobalFileViewMode) === FileViewMode.List;

  const mergeConflictsMessage = intl.formatMessage(messages.mergeConflicts);

  const targetBranchHash = useSelector(getPullRequestDestinationHash) || '';
  const sourceBranchHash = useSelector(getPullRequestSourceHash) || '';
  const targetBranchShortHash = shortHash(targetBranchHash) || '';
  const featureBranchShortHash = shortHash(sourceBranchHash);
  const diffCommand = scmCommandCode(
    targetBranchShortHash,
    featureBranchShortHash
  );
  const isColorBlindModeEnabled = useSelector(getGlobalIsColorBlindModeEnabled);

  const publishPrAnalyticsFile = usePrFileViewFileAnalytics();
  const onFileClickAnalytics = useCallback(
    (event: React.MouseEvent, clickProps: FileClickProps) => {
      onFileClick!(event, clickProps);
      publishPrAnalyticsFile(
        clickProps.name,
        clickProps.isConflicted,
        clickProps.fileDiffStatus
      );
    },
    [onFileClick, publishPrAnalyticsFile]
  );

  const [filterString, setFilterString] = useState<string>('');

  const filteredFileTree = fileTree
    ? filterFileEntries(fileTree, filterString)
    : fileTree;

  type LinesUpdated = {
    linesAdded: number;
    linesRemoved: number;
  };

  const countLinesUpdated = (contents: TreeEntry[]): LinesUpdated =>
    contents.reduce(
      (totalCount, entry) => {
        const { linesAdded, linesRemoved } = isFile(entry)
          ? { linesAdded: entry.linesAdded, linesRemoved: entry.linesRemoved }
          : countLinesUpdated(entry.contents);

        return {
          linesAdded: totalCount.linesAdded + linesAdded,
          linesRemoved: totalCount.linesRemoved + linesRemoved,
        };
      },
      { linesAdded: 0, linesRemoved: 0 }
    );

  const { linesAdded, linesRemoved } = fileTree
    ? countLinesUpdated(fileTree)
    : { linesAdded: 0, linesRemoved: 0 };

  if (isCollapsed) {
    const content = intl.formatMessage(messages.fileCount, {
      total: untruncatedFileCount,
      formattedCount: untruncatedFileCount,
    });

    return (
      <Tooltip position="left" content={content}>
        <Button
          appearance="subtle"
          iconBefore={
            <styles.IconContainer>
              {icon}{' '}
              {hasConflicts && (
                <styles.ConflictIndicator aria-label={mergeConflictsMessage} />
              )}
            </styles.IconContainer>
          }
        />
      </Tooltip>
    );
  }

  return (
    <>
      {!!fileTree && !!fileTree.length && !hasError && (
        <>
          {(linesAdded > 0 || linesRemoved > 0) && (
            <>
              {isChangeFileTreeFilteringEnabled && (
                <FilterControl onFilterChange={setFilterString} />
              )}
              <styles.LinesUpdatedPanel>
                <styles.LinesUpdatedBody>
                  <styles.LinesUpdatedLabel>
                    <Heading size="xsmall">
                      {intl.formatMessage(messages.linesUpdatedLabel)}
                    </Heading>
                  </styles.LinesUpdatedLabel>
                  <styles.LinesUpdatedIcons>
                    {linesAdded > 0 && (
                      <styles.LinesAdded
                        cutCorners={linesRemoved > 0}
                        aria-hidden="true"
                        isColorBlindMode={isColorBlindModeEnabled}
                      >
                        <Text
                          size="UNSAFE_small"
                          weight="semibold"
                          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
                          color="color.text.accent.green"
                        >
                          +{linesAdded}
                        </Text>
                      </styles.LinesAdded>
                    )}
                    {linesRemoved > 0 && (
                      <styles.LinesRemoved
                        cutCorners={linesAdded > 0}
                        aria-hidden="true"
                        isColorBlindMode={isColorBlindModeEnabled}
                      >
                        <Text
                          size="UNSAFE_small"
                          weight="semibold"
                          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
                          color="color.text.accent.red"
                        >
                          -{linesRemoved}
                        </Text>
                      </styles.LinesRemoved>
                    )}
                  </styles.LinesUpdatedIcons>
                </styles.LinesUpdatedBody>
              </styles.LinesUpdatedPanel>
            </>
          )}
          {shouldShowFileList ? (
            <FileList
              fileTree={
                isChangeFileTreeFilteringEnabled ? filteredFileTree : fileTree
              }
              expandAll
              onClick={onFileClickAnalytics}
            />
          ) : (
            <FileTree
              fileTree={
                isChangeFileTreeFilteringEnabled ? filteredFileTree : fileTree
              }
              expandAll
              onClick={onFileClickAnalytics}
            />
          )}
          {isTruncated && (
            <SectionMessage appearance="information">
              <Heading size="small">
                {intl.formatMessage(messages.truncatedContentsMessageTitle)}
              </Heading>
              <FormattedMessage
                {...messages.truncatedContentsMessageMain}
                tagName="p"
              />
              <p>
                <CopyButton
                  shouldMatchTextFieldHeight={false}
                  tooltipPosition="bottom"
                  value={diffCommand}
                >
                  {intl.formatMessage(messages.truncatedContentsButtonText)}
                </CopyButton>
              </p>
            </SectionMessage>
          )}
        </>
      )}
      {hasError && (
        <GenericMessage
          iconType="warning"
          title={<FormattedMessage {...messages.errorHeading} />}
        >
          <CustomThemeButton appearance="link" onClick={onErrorClick}>
            <FormattedMessage {...commonMessages.tryAgain} />
          </CustomThemeButton>
        </GenericMessage>
      )}
    </>
  );
});

function filterFileEntries(fileTree: TreeEntry[], filter: string): TreeEntry[] {
  if (!filter) return fileTree;

  if (filter[0] === '!') {
    // Treat filter as a negation filter
    const negationFilter = filter.slice(1).toLowerCase();

    if (negationFilter.length === 0) {
      return fileTree;
    }

    function filterEntryNegation(entry: TreeEntry): TreeEntry | null {
      const entryName = entry.name.toLowerCase();

      // If entry matches the negation filter, exclude it
      if (entryName.includes(negationFilter)) {
        return null;
      }

      if (entry.type === 'file') {
        return entry;
      }

      // For directories, recursively filter contents
      const filteredContents = entry.contents
        .map(child => filterEntryNegation(child))
        .filter((child): child is TreeEntry => child !== null);

      // Keep directory if it has any non-filtered children
      return filteredContents.length > 0
        ? {
            ...entry,
            contents: filteredContents,
          }
        : null;
    }

    return fileTree
      .map(entry => filterEntryNegation(entry))
      .filter((entry): entry is TreeEntry => entry !== null);
  }

  // Support filtering paths like "src/components/card/"
  const filterSegments = filter
    .split('/')
    .map(segment => segment.toLowerCase())
    .filter(segment => segment.length > 0);

  function matchesFilterPathSegments(
    entry: TreeEntry,
    segments: string[],
    depth: number
  ): boolean {
    if (depth >= segments.length) return true;

    const currentSegment = segments[depth];
    const entryName = entry.name.toLowerCase();
    if (!entryName.includes(currentSegment)) return false;
    if (depth === segments.length - 1) return true;
    if (entry.type === 'directory') {
      return entry.contents.some(child =>
        matchesFilterPathSegments(child, segments, depth + 1)
      );
    }
    return false;
  }

  function filterEntry(entry: TreeEntry, depth = 0): TreeEntry | null {
    const isEntryPathMatchingFilterPath = matchesFilterPathSegments(
      entry,
      filterSegments,
      depth
    );

    if (entry.type === 'file') {
      return isEntryPathMatchingFilterPath ? entry : null;
    }

    let filteredContents: TreeEntry[] = [];
    if (isEntryPathMatchingFilterPath && depth === filterSegments.length - 1) {
      filteredContents = entry.contents;
    } else {
      filteredContents = entry.contents
        .map(child =>
          filterEntry(child, depth + (isEntryPathMatchingFilterPath ? 1 : 0))
        )
        .filter((child): child is TreeEntry => child !== null);
    }

    // Keep directory if it matches or has matching children
    if (isEntryPathMatchingFilterPath || filteredContents.length > 0) {
      return {
        ...entry,
        contents: filteredContents,
      };
    }
    return null;
  }

  return fileTree
    .map(entry => filterEntry(entry))
    .filter((entry): entry is TreeEntry => entry !== null);
}
